import { CornerDownLeft } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useEditorMode } from "../../hooks";

interface MentionListProps {
  items: string[];
  command: (params: { id: string }) => void;
  query?: string;
}

export interface MentionListHandle {
  onKeyDown: (params: { event: KeyboardEvent }) => boolean;
}
const CreateHint = ({
  className,
  variableText,
  variant,
  onClick,
  onMouseEnter,
}: {
  className?: string;
  variableText: string;
  variant?: "selected";
  onClick?: () => void;
  onMouseEnter?: () => void;
}) => (
  <button
    type="button"
    className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-gray-500 ${
      variant === "selected" ? "bg-gray-100 text-gray-900" : ""
    } hover:bg-gray-100 hover:text-gray-900 focus:bg-gray-100 focus:text-gray-900 focus:outline-none ${
      className || ""
    }`}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    tabIndex={-1}
  >
    <span>
      Create{" "}
      <strong
        className={variant === "selected" ? "text-gray-900" : "text-black"}
      >
        {variableText}
      </strong>
    </span>
    <kbd
      className={`ml-2 inline-flex aspect-square h-5 w-5 items-center justify-center rounded-md border px-1 py-0.5 text-xs font-semibold ${
        variant === "selected"
          ? "border-gray-400 bg-gray-200 text-gray-700"
          : "border-gray-300 bg-gray-100 text-gray-500"
      }`}
    >
      <CornerDownLeft />
    </kbd>
  </button>
);

const MentionList = forwardRef<MentionListHandle, MentionListProps>(
  (props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const currentQuery = props.query || "";

    // Total items includes the CreateHint
    const totalItems = props.items.length + 1;

    const selectItem = (index: number) => {
      if (index < props.items.length) {
        // Existing item selected
        props.command({ id: props.items[index] });
      } else {
        // CreateHint selected
        const variableName = currentQuery || "variableName";
        props.command({ id: variableName });
      }
    };

    const upHandler = () => {
      setSelectedIndex((selectedIndex + totalItems - 1) % totalItems);
    };

    const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % totalItems);
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    useEffect(() => setSelectedIndex(0), [props.items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === "ArrowUp") {
          upHandler();
          return true;
        }

        if (event.key === "ArrowDown") {
          downHandler();
          return true;
        }

        if (event.key === "Enter" || event.key === "Tab") {
          event.preventDefault();
          event.stopPropagation();
          enterHandler();
          return true;
        }

        if (event.key === "Escape") {
          event.preventDefault();
          return true;
        }

        return false;
      },
    }));

    const mode = useEditorMode();
    const variableText =
      mode === "jinja2"
        ? `{{${currentQuery || "variableName"}}}`
        : `{${currentQuery || "variableName"}}`;

    return (
      <div className="min-w-[200px] rounded-md border border-gray-200 bg-white p-1 shadow-md">
        {props.items.length ? (
          <>
            {props.items.map((item: string, index: number) => (
              <button
                className={`w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                  index === selectedIndex
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-900"
                }`}
                key={index}
                onClick={() => selectItem(index)}
                onMouseEnter={() => setSelectedIndex(index)}
                tabIndex={-1}
              >
                <span className="font-medium">{item}</span>
              </button>
            ))}
            <div className="mt-1 border-t border-gray-100 pt-1">
              <CreateHint
                className="text-xs"
                variableText={variableText}
                variant={
                  selectedIndex === props.items.length ? "selected" : undefined
                }
                onClick={() => selectItem(props.items.length)}
                onMouseEnter={() => setSelectedIndex(props.items.length)}
              />
            </div>
          </>
        ) : (
          <CreateHint
            className="text-sm"
            variableText={variableText}
            variant="selected"
            onClick={() => selectItem(0)}
            onMouseEnter={() => setSelectedIndex(0)}
          />
        )}
      </div>
    );
  },
);

MentionList.displayName = "MentionList";
export default MentionList;
